import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { EmptyState } from '../../components/common/EmptyState';
import { PAGE_COLORS } from '../../utils/pageColors';
import {
  useGetUserProfilesQuery,
  useChangeUserStatusMutation,
  useSoftDeleteUserProfileMutation,
} from '../../store/slices/apiSlice';
import type { UserProfile } from '../../utils/types';
import { useToast } from '../../hooks/useToast';
import { CompleteJobConfirmDialog } from '../../components/production-line/CompleteJobConfirmDialog';

type StatusFilter = 'all' | 'active' | 'inactive' | 'blocked' | 'deleted';

interface UserListPageProps {}

export const UserListPage: React.FC<UserListPageProps> = () => {
  const toast = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [orderBy, setOrderBy] = useState<string>('-created_at');
  const [pendingDelete, setPendingDelete] = useState<UserProfile | null>(null);

  const {
    data: userProfilesResponse,
    isLoading,
    error,
    refetch,
  } = useGetUserProfilesQuery({
    page: currentPage,
    page_size: 20,
    search: searchTerm || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    role: roleFilter || undefined,
    ordering: orderBy,
  });

  const [changeUserStatus] = useChangeUserStatusMutation();
  const [softDeleteUser, { isLoading: isSoftDeleting }] = useSoftDeleteUserProfileMutation();

  const handleStatusChange = async (user: UserProfile, newStatus: 'active' | 'inactive') => {
    try {
      await changeUserStatus({
        profileId: user.id,
        status: newStatus,
      }).unwrap();

      toast.success('Status updated', `User status changed to ${newStatus}.`);
      refetch();
    } catch (err: any) {
      toast.error('Update failed', err?.data?.error || 'Failed to change user status');
    }
  };

  const handleConfirmSoftDelete = async () => {
    const profile = pendingDelete;
    if (!profile) return;
    try {
      await softDeleteUser({ profileId: profile.id }).unwrap();
      setPendingDelete(null);
      toast.success(
        'User deleted',
        'Username and email can be reused when you create a new account.',
      );
    } catch (err: any) {
      toast.error('Delete failed', err?.data?.error || 'Failed to delete user');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setRoleFilter('');
    setCurrentPage(1);
  };

  const getStatusBadge = (s: UserProfile['status']) => {
    const badges: Record<string, string> = {
      active: 'bg-green-100 text-green-800 border border-green-200',
      inactive: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      blocked: 'bg-red-100 text-red-800 border border-red-200',
      deleted: 'bg-gray-200 text-gray-800 border border-gray-300',
    };
    return badges[s] || badges.active;
  };

  const getStatusIcon = (s: UserProfile['status']) => {
    switch (s) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'inactive':
        return <XCircle className="w-4 h-4" />;
      case 'blocked':
        return <AlertTriangle className="w-4 h-4" />;
      case 'deleted':
        return <Trash2 className="w-4 h-4" />;
      default:
        return <CheckCircle className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <AppShell>
        <PageLayout
          title="User Management"
          subtitle="Manage system users and their profiles"
          bgColor={PAGE_COLORS.settings.bgColor}
          textColor={PAGE_COLORS.settings.textColor}
          borderColor={PAGE_COLORS.settings.borderColor}
          icon={<Users className="w-6 h-6" />}
        >
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="large" />
          </div>
        </PageLayout>
      </AppShell>
    );
  }

  if (error) {
    const isPermissionError = 'status' in error && error.status === 403;

    return (
      <AppShell>
        <PageLayout
          title="User Management"
          subtitle="Manage system users and their profiles"
          bgColor={PAGE_COLORS.settings.bgColor}
          textColor={PAGE_COLORS.settings.textColor}
          borderColor={PAGE_COLORS.settings.borderColor}
          icon={<Users className="w-6 h-6" />}
        >
          <ContentCard title={isPermissionError ? 'Access Denied' : 'Error Loading Users'}>
            <div className="text-center py-8">
              {isPermissionError ? (
                <Shield className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              ) : (
                <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              )}
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {isPermissionError ? 'Permission Required' : 'Failed to load users'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isPermissionError
                  ? "You don't have permission to view the user management page. Please contact your administrator to request the required 'users_view' permission."
                  : 'There was an error loading the user list. Please try again.'}
              </p>
              {!isPermissionError && (
                <Button onClick={() => refetch()}>
                  Try Again
                </Button>
              )}
            </div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  const users = userProfilesResponse?.results || [];
  const totalCount = userProfilesResponse?.count || 0;
  const hasUsers = users.length > 0;
  const totalPages = Math.ceil(totalCount / 20);

  return (
    <AppShell>
      <PageLayout
        title="User Management"
        subtitle={`${totalCount} total users`}
        bgColor={PAGE_COLORS.settings.bgColor}
        textColor={PAGE_COLORS.settings.textColor}
        borderColor={PAGE_COLORS.settings.borderColor}
        icon={<Users className="w-6 h-6" />}
      >
        <CompleteJobConfirmDialog
          isOpen={!!pendingDelete}
          title="Delete user?"
          leadContent={
            pendingDelete ? (
              <>
                Remove access for{' '}
                <strong className="font-semibold text-gray-900">
                  {pendingDelete.full_name || pendingDelete.user_details.username}
                </strong>{' '}
                (@{pendingDelete.user_details.username})? This is a soft delete — their username and email
                can be used again for a new account.
              </>
            ) : null
          }
          sectionHeading="This will:"
          bullets={[
            'Disable login for this account',
            'Keep the record visible as Deleted for audit',
            'Free their username and email for a new user',
          ]}
          cancelLabel="Cancel"
          confirmLabel="Delete user"
          confirmingLabel="Deleting…"
          destructive
          isLoading={isSoftDeleting}
          onCancel={() => {
            if (!isSoftDeleting) setPendingDelete(null);
          }}
          onConfirm={() => {
            void handleConfirmSoftDelete();
          }}
          closeOnBackdropClick
        />

        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <Button type="submit" size="sm">
                  Search
                </Button>
              </form>

              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="blocked">Blocked (legacy)</option>
                  <option value="deleted">Deleted</option>
                </select>

                <select
                  value={orderBy}
                  onChange={(e) => setOrderBy(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="-created_at">Newest First</option>
                  <option value="created_at">Oldest First</option>
                  <option value="user__username">Username A-Z</option>
                  <option value="-user__username">Username Z-A</option>
                  <option value="user__last_name">Last Name A-Z</option>
                  <option value="-user__last_name">Last Name Z-A</option>
                </select>

                {(searchTerm || statusFilter !== 'all' || roleFilter || orderBy !== '-created_at') && (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Link to="/users/create">
                <Button icon={<Plus className="w-4 h-4" />}>
                  Create User
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <ContentCard title="User Profiles">
          {!hasUsers && !isLoading ? (
            <EmptyState
              icon={<Users className="w-12 h-12" />}
              title="No users found"
              description={
                searchTerm || statusFilter !== 'all'
                  ? 'No users match your current filters.'
                  : 'No users have been created yet.'
              }
              action={
                <Link to="/users/create">
                  <Button icon={<Plus className="w-4 h-4" />}>
                    Create First User
                  </Button>
                </Link>
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => {
                      const isDeleted = user.status === 'deleted';
                      return (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                                  <span className="text-white font-medium text-sm">
                                    {user.user_details.first_name?.[0]}
                                    {user.user_details.last_name?.[0]}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.full_name || 'No name provided'}
                                </div>
                                <div className="text-sm text-gray-500">@{user.user_details.username}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{user.user_details.email}</div>
                            {user.phone && (
                              <div className="text-sm text-gray-500">{user.phone}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {user.role_details ? (
                              <div className="flex items-center">
                                <Shield className="w-4 h-4 text-blue-500 mr-2" />
                                <span className="text-sm text-gray-900">{user.role_details.name}</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">No role assigned</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(user.status)}`}
                            >
                              {getStatusIcon(user.status)}
                              <span className="ml-1 capitalize">{user.status}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/users/${user.id}`}
                                className="text-blue-600 hover:text-blue-900"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </Link>
                              {!isDeleted && (
                                <>
                                  <Link
                                    to={`/users/${user.id}/edit`}
                                    className="text-green-600 hover:text-green-900"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Link>
                                  {user.status !== 'active' && (
                                    <button
                                      type="button"
                                      onClick={() => handleStatusChange(user, 'active')}
                                      className="text-green-600 hover:text-green-900"
                                      title="Activate User"
                                    >
                                      <UserCheck className="w-4 h-4" />
                                    </button>
                                  )}
                                  {user.status === 'active' && (
                                    <button
                                      type="button"
                                      onClick={() => handleStatusChange(user, 'inactive')}
                                      className="text-yellow-600 hover:text-yellow-900"
                                      title="Deactivate User"
                                    >
                                      <UserX className="w-4 h-4" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => setPendingDelete(user)}
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete user"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t border-gray-200">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <Button
                      variant="outline"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(currentPage * 20, totalCount)}</span> of{' '}
                        <span className="font-medium">{totalCount}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </Button>
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </Button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </ContentCard>
      </PageLayout>
    </AppShell>
  );
};
